import { Bound } from "lib/@components/binding/Bound"
import { Box, TableCell, TableRow, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSkills } from "routes/@lib/skills"
import { Repeat } from "lib/@components/repeat"
import { ScheduleWeeks } from "routes/plan/plugins/weekly-planner/plugins/planner-schedule-list/schedule-weeks"
import { PlannerItems } from "routes/plan/plugins/weekly-planner/plugins/planner-skills-schedule/planner-items"
import { memo, useMemo } from "react"

const CachedPlannerSkillsList = memo(function PlannerSkillsList({ days }) {
    const [index] = useSkills()
    const skills = days
        .flatMap((d) => d.tasks)
        .map("skillSet")
        .unique()
        .map((c) => index[c])
        .sortBy("Skilling")

    return <Repeat list={skills} item={<SkillWeeks />} />
})

export function PlannerSkillsList() {
    const { days } = useBoundContext()
    return <CachedPlannerSkillsList days={days} />
}

function SkillWeeks({ item }) {
    const { days } = useBoundContext()
    const [passedOnDays, allSchedules, allScheduleIndex] = useMemo(() => {
        const passedOnDays = structuredClone(days)
        for (const day of passedOnDays) {
            day.tasks = day.tasks.filter((c) => c.skillSet === item.CoreSkillingID)
            day.schedules.forEach(
                (schedule) => (schedule.tasks = schedule.tasks.filter((c) => c.skillCode === item.SkillingCode))
            )
            day.schedules = day.schedules.filter((s) => s.tasks.length)
            day.schedules.forEach((schedule) => {
                const colors = schedule.tasks.reduce(
                    (a, c) => {
                        const classification = c.classification.toLowerCase()
                        return {
                            ...a,
                            [classification]: (a[classification] ?? 0) + 1,
                            minutes: (a.minutes ?? 0) + c.minutes ?? 0,
                        }
                    },
                    { red: 0, amber: 0, green: 0, pink: 0, minutes: 0 }
                )
                Object.assign(schedule, colors)
            })
            Object.assign(
                day,
                day.schedules.reduce(
                    (a, c) => ({
                        red: a.red + c.red,
                        green: a.green + c.green,
                        pink: a.pink + c.pink,
                        amber: a.amber + c.amber,
                        minutes: a.minutes + c.minutes,
                    }),
                    { red: 0, amber: 0, pink: 0, green: 0, minutes: 0 }
                )
            )
        }
        const allScheduleIndex = passedOnDays.reduce((a, r) => {
            for (const schedule of r.schedules) {
                a[schedule.scheduleId] = schedule
            }
            return a
        }, {})

        const allSchedules = Object.keys(allScheduleIndex)
            .sortBy((c) => allScheduleIndex[c].fullPath.split("-").slice(1).join("-"))
            .sortBy((c) => allScheduleIndex[c].fullPath.split("-")[0])
        return [passedOnDays, allSchedules, allScheduleIndex]
    }, [days, item.CoreSkillingID, item.SkillingCode])

    return (
        <Bound
            days={passedOnDays}
            skill={item}
            allSchedules={allSchedules}
            allScheduleIndex={allScheduleIndex}
            top={70}
        >
            <PlannerItems.Item>
                <TableRow sx={{ position: "sticky", top: 36, zIndex: 7 }}>
                    <TableCell
                        sx={{
                            whiteSpace: "nowrap",
                            position: "sticky",
                            left: 0,
                            bgcolor: "background.paper",
                        }}
                    >
                        <Typography variant="subtitle2">{item.Skilling}</Typography>
                    </TableCell>
                    <TableCell colSpan={52} sx={{ bgcolor: "background.paper" }}>
                        <Box position="absolute" left={0} top={0} bottom={0} right={0} bgcolor="action.selected" />
                    </TableCell>
                </TableRow>
            </PlannerItems.Item>
            <Repeat list={allSchedules} item={<ScheduleWeeks />} />
        </Bound>
    )
}
